import { Switch } from "@headlessui/react";
import React, { useCallback, useEffect, useState } from "react";
import { RouteComponentProps } from "react-router-dom";
import ExternalLink from "../../../../components/ExternalLink";
import { useBillingPlans } from "../../../../context/billingPlans";
import { useToast } from "../../../../context/toast";
import { useWorkspaces } from "../../../../context/workspaces";
import { ApiResponse, fetchApi } from "../../../../utils/api";
import classNames from "../../../../utils/classNames";
import { PlanType } from "../../../../utils/types";

interface Props extends RouteComponentProps<{}> {}

const HostDashboardPage: React.FC<Props> = () => {
  const { setModalUpgradeConfig, addToast } = useToast();
  const { getPlanByType, setNewPlan, currentPlan } = useBillingPlans();
  const { currentWorkspaceId } = useWorkspaces();
  const [hostDashboardIsEnabled, setHostDashboardIsEnabled] =
    useState<boolean>(false);

  const fetchHostDashboardIsEnabled = useCallback(async () => {
    if (!currentWorkspaceId) return;

    try {
      const response = await fetchApi(
        `api/workspace/${currentWorkspaceId}/host/enable`
      );

      const apiResponse: ApiResponse<{ enable_dashboard_host: boolean }> =
        await response.json();

      if (apiResponse.status) {
        setHostDashboardIsEnabled(apiResponse.data.enable_dashboard_host);
      }
    } catch {
      setHostDashboardIsEnabled(false);
    }
  }, [currentWorkspaceId]);

  useEffect(() => {
    fetchHostDashboardIsEnabled();
  }, [fetchHostDashboardIsEnabled]);

  const toggleHostDashboard = useCallback(
    async (value: boolean) => {
      if (currentPlan?.type !== PlanType.business) {
        setModalUpgradeConfig({
          open: true,
          resolve: () => {
            setNewPlan((prevState) => ({
              ...prevState,
              plan: getPlanByType(PlanType.business)
            }));
          },
          nextPlan: getPlanByType(PlanType.business),
          featureTitle: "Host Dashboard",
          featureDescription:
            "This feature is only available in workspaces with a business plan. Upgrade your plan to unlock this feature."
        });
      }

      if (!currentWorkspaceId) return;

      setHostDashboardIsEnabled(value);

      try {
        const response = await fetchApi(
          `api/workspace/${currentWorkspaceId}/host/${value}`,
          {
            method: "PUT"
          }
        );

        const apiResponse: ApiResponse<[]> = await response.json();

        if (!apiResponse.status) {
          setHostDashboardIsEnabled(!value);
          addToast({
            type: "error",
            message: apiResponse.message
          });
        }
      } catch {
        setHostDashboardIsEnabled(!value);
      }
    },
    [
      addToast,
      currentPlan?.type,
      currentWorkspaceId,
      getPlanByType,
      setModalUpgradeConfig,
      setNewPlan
    ]
  );

  return (
    <>
      <div className="flex flex-col w-full">
        <div className="mb-6">
          <h1 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
            Host Dashboard
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-300">
            With the{" "}
            <ExternalLink
              className="inline-flex"
              text="Host Dashboard"
              url="https://docs.hotspotty.net/advanced/host-dashboard"
            />{" "}
            your contacts (hosts, partners, ...) can view their data through a
            dedicated website.
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-300">
            Commissions, payment methods, registered payments, ... can be
            consulted on{" "}
            <ExternalLink
              className="inline-flex"
              text="host.hotspotty.net"
              url="https://host.hotspotty.net"
            />
            .
          </p>
        </div>

        <Switch.Group
          as="li"
          className="py-4 flex items-center justify-between"
        >
          <div className="flex flex-col w-8/12">
            <Switch.Label
              as="div"
              className="text-sm font-medium mb-1 text-gray-900 dark:text-white"
              passive
            >
              Activate Host Dashboard
            </Switch.Label>
            <Switch.Description className="text-sm text-gray-500 dark:text-gray-200">
              Allow{" "}
              <ExternalLink
                className="inline-flex"
                text="Host Dashboard "
                url="https://host.hotspotty.net"
              />{" "}
              access via the email address linked to each contact
            </Switch.Description>
          </div>
          <Switch
            checked={hostDashboardIsEnabled}
            onChange={() => toggleHostDashboard(!hostDashboardIsEnabled)}
            className={classNames(
              hostDashboardIsEnabled
                ? "bg-hotspotty-400 dark:bg-gray-100 dark:hover:bg-gray-200"
                : "bg-gray-200 dark:bg-gray-500 dark:hover:bg-gray-400",
              "ml-4 relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-light-hotspotty-400"
            )}
          >
            <span className="sr-only">Host Dashboard active/inactive</span>
            <span
              aria-hidden="true"
              className={classNames(
                hostDashboardIsEnabled ? "translate-x-5" : "translate-x-0",
                "inline-block h-5 w-5 rounded-full bg-white dark:bg-gray-800 shadow transform ring-0 transition ease-in-out duration-200"
              )}
            />
          </Switch>
        </Switch.Group>
      </div>
    </>
  );
};

export default HostDashboardPage;
